import axios from "axios";
import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import { IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import TablePagination from "@mui/material/TablePagination";
import TableContainer from "@mui/material/TableContainer";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import TableBody from "@mui/material/TableBody";
import REACT_APP from "../../environment";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CreateEmployee from "./CreateEmployee";
import TerminateModal from "./TerminateModal";
import { visuallyHidden } from "@mui/utils";
import Paper from "@mui/material/Paper";
import AddEmpoyees from "./AddEmpoyees";
import Loading from "../Loading";
import Error from "../Error";
import { useNavigate } from "react-router-dom";
import CreateEmployeeWithCSV from "./CreateEmployeeWithCSV";
import { useTranslation } from "react-i18next";

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    { id: "employee_id", numeric: false, label: "Employee ID" },
    { id: "first_name", numeric: true, label: "First Name" },
    { id: "last_name", numeric: true, label: "Last Name" },
    { id: "position", numeric: true, label: "Position" },
    { id: "department", numeric: true, label: "Department" },
    { id: "email", numeric: true, label: "Email" },
    { id: "rt_first_name", colSpan: 2, numeric: true, label: "Reports To" },
    { id: "action", numeric: true, label: "Action" },
];

function EnhancedTableHead(props) {
    const { t } = useTranslation();
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell key={headCell.id} align="left" sortDirection={orderBy === headCell.id ? order : false}>
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : "asc"}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {t(headCell.label)}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

function EmployeesList() {
    const { t } = useTranslation();
    const [id, setId] = React.useState(null);
    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("calories");
    const [page, setPage] = React.useState(0);
    const [data, setData] = React.useState([]);
    const [idx, setIdx] = React.useState("");
    const [loading, setLoading] = React.useState(true);
    const [errorMsg, setErrorMsg] = React.useState(null);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);

    const [anchor, setAnchor] = React.useState(null);

    const closeMenu = () => {
        setAnchor(null);
    };

    const getDate = async () => {
        setErrorMsg(null);
        // setLoading(true);
        axios
            .get(`${REACT_APP.URL}/api/hrp/v1/employee/edit/all`, {
                headers: { Authorization: sessionStorage.getItem("token") },
            })
            .then((res) => {
                setData(res.data.data);
                setLoading(false);
            })
            .catch((err) => {
                setErrorMsg(err.message);
                setLoading(false);
            });
    };

    const handleSearch = (search) => {
        if (search.length) {
            axios
                .get(`${REACT_APP.URL}/api/hrp/v1/employee/edit/all?search=${search}`, { headers: { Authorization: sessionStorage.getItem("token") } })
                .then((res) => {
                    setData(res.data.data);
                })
                .catch((err) => {
                    setErrorMsg(err.message);
                });
        } else {
            getDate();
        }
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const openMenu = (event, elem) => {
        setIdx(elem);
        setAnchor(event.currentTarget);
    };
    React.useEffect(() => {
        getDate();
    }, []);
    const navigate = useNavigate();
    const onContacts = () => {
        navigate(`${idx.id}/contacts`);
    };
    const onEducations = () => {
        navigate(`${idx.id}/educations`);
    };

    const onAddress = () => {
        navigate(`${idx.id}/address`);
    };
    const onDependency = () => {
        navigate(`${idx.id}/dependency`);
    };
    const onWork = () => {
        navigate(`${idx.id}/work`);
    };

    return (
        <>
            {loading ? (
                <Loading />
            ) : errorMsg ? (
                <Error message={errorMsg} />
            ) : (
                <div className="py-5">
                    <div className="d-flex justify-content-between row pb-3">
                        <div className="col-sm-4">
                            <h4 className="text-center text-sm-start">{t("Employees")}</h4>
                        </div>
                        <div className="col-sm-4 position-relative">
                            <input
                                onChange={(e) => handleSearch(e.target.value)}
                                style={{ paddingLeft: "35px" }}
                                type="text"
                                className="form-control"
                                placeholder={t("Search")}
                            />
                            <SearchIcon sx={{ top: "8px", left: "20px", color: "#aaa", fontSize: "23px", position: "absolute" }} />
                        </div>
                    </div>
                    <Box sx={{ width: "100%" }}>
                        <Paper sx={{ width: "100%", mb: 2 }}>
                            <TableContainer>
                                <Table sx={{ minWidth: 750, px: 2 }} aria-labelledby="tableTitle" size="medium">
                                    <EnhancedTableHead order={order} orderBy={orderBy} rowCount={data.length} onRequestSort={handleRequestSort} />
                                    <TableBody>
                                        {stableSort(data, getComparator(order, orderBy))
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((row, index) => {
                                                return (
                                                    <TableRow hover tabIndex={-1} key={row.id}>
                                                        <TableCell align="left">{row.employee_id}</TableCell>
                                                        <TableCell align="left">{row.first_name}</TableCell>
                                                        <TableCell align="left">{row.last_name}</TableCell>
                                                        <TableCell align="left">{row.position}</TableCell>
                                                        <TableCell align="left">{row.department}</TableCell>
                                                        <TableCell align="left">{row.email}</TableCell>
                                                        <TableCell align="left">
                                                            {row.reports_to === null ? "" : `${row.rt_first_name}  ${row.rt_last_name}`}
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            <Box sx={{ flexGrow: 0 }}>
                                                                <Tooltip title={t("Action")}>
                                                                    <IconButton
                                                                        style={{ borderRadius: "50%", padding: "2px" }}
                                                                        onClick={(e, elem = row) => openMenu(e, elem)}
                                                                        sx={{ p: 0 }}
                                                                    >
                                                                        <MoreVertIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Menu
                                                                    onClick={() => setId(row.id)}
                                                                    sx={{ mt: "23px" }}
                                                                    id="menu-appbar"
                                                                    anchorOrigin={{ vertical: "top", horizontal: "left" }}
                                                                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                                                                    anchorEl={anchor}
                                                                    open={Boolean(anchor)}
                                                                    onClose={closeMenu}
                                                                >
                                                                    <MenuItem
                                                                        className="m__item"
                                                                        data-bs-toggle="modal"
                                                                        data-bs-target="#viewModal"
                                                                        onClick={() => setAnchor(null)}
                                                                    >
                                                                        <Typography>{t("Terminate")}</Typography>
                                                                    </MenuItem>
                                                                    <MenuItem className="m__item" onClick={(e) => onContacts(id)}>
                                                                        <Typography>{t("Review contact")}</Typography>
                                                                    </MenuItem>
                                                                    <MenuItem className="m__item" onClick={(e) => onEducations(id)}>
                                                                        <Typography>{t("Review education")}</Typography>
                                                                    </MenuItem>
                                                                    <MenuItem className="m__item" onClick={(e) => onAddress(id)}>
                                                                        <Typography>{t("Review address")}</Typography>
                                                                    </MenuItem>
                                                                    <MenuItem className="m__item" onClick={(e) => onDependency(id)}>
                                                                        <Typography>{t("Review dependency")}</Typography>
                                                                    </MenuItem>
                                                                    <MenuItem className="m__item" onClick={(e) => onWork(id)}>
                                                                        <Typography>{t("Review work history")}</Typography>
                                                                    </MenuItem>
                                                                </Menu>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                page={page}
                                component="div"
                                labelRowsPerPage={t("Rows per page")}
                                count={data.length}
                                rowsPerPage={rowsPerPage}
                                onPageChange={handleChangePage}
                                rowsPerPageOptions={[5, 10, 25, 100]}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Paper>
                    </Box>
                    <div className="d-flex g-1">
                        <CreateEmployee getDate={getDate} />
                        <AddEmpoyees getDate={getDate} />
                    </div>
                    <div className="d-flex g-1 mt-2">
                        <CreateEmployeeWithCSV getDate={getDate} />
                    </div>

                    {idx ? <TerminateModal data={idx} getDate={getDate} /> : null}
                </div>
            )}
        </>
    );
}

export default React.memo(EmployeesList);
